import { forwardRef } from 'react';
import type { ReactElement } from 'react';

import { DotsThreeIcon, MinusRectangleIcon } from '@natter-co/design-system';
// `dist/lib/*` rather than `./lib`: the package's `./lib` export points at a `dist/lib/index.es.js` it
// does not ship.
import IconBase from '@phosphor-icons/react/dist/lib/IconBase';

import type { Icon as PhosphorIcon, IconProps, IconWeight } from '@phosphor-icons/react';

/**
 * DS 2.0 glyphs wrapped as Phosphor icons, for `ICON_SWAPS` rows where DS 2.0 redrew the Phosphor
 * glyph. Built on Phosphor's own `IconBase`, so props, grid and ref forwarding match every other swap.
 *
 * The DS glyph is drawn on the same `0 0 256 256` grid, so it nests at full size and stays the single
 * source of its geometry. Every weight resolves to it, so a `swapTo(…, 'bold')` cannot render blank.
 *
 * The cast is only because `common/typings/react.d.ts` narrows `forwardRef`'s return type, dropping
 * the `ForwardRefExoticComponent` brand Phosphor's `Icon` type requires.
 */
const createGlyph = (glyph: ReactElement, displayName: string) => {
  const weights = new Map<IconWeight, ReactElement>(
    (['thin', 'light', 'regular', 'bold', 'fill', 'duotone'] as const).map((weight) => [weight, glyph])
  );
  const Glyph = forwardRef<SVGSVGElement, IconProps>((props, ref) => (
    <IconBase ref={ref} {...props} weights={weights} />
  )) as PhosphorIcon;
  Glyph.displayName = displayName;

  return Glyph;
};

const DOTS = <DotsThreeIcon size={256} />;

/** Phosphor's `dots-three` draws its dots at about half the size of the DS 2.0 `DotsThree`. */
export const NatterDotsThree = createGlyph(DOTS, 'NatterDotsThree');

/** DS 2.0 has no vertical overflow glyph, so ⋮ is the same dots turned upright. */
export const NatterDotsThreeVertical = createGlyph(
  <g transform="rotate(90 128 128)">{DOTS}</g>,
  'NatterDotsThreeVertical'
);

/** Natter-drawn "step off the stage": a rectangle with a minus. Phosphor has no equivalent. */
export const NatterMinusRectangle = createGlyph(<MinusRectangleIcon size={256} />, 'NatterMinusRectangle');
